import React, {
  useContext,
  createContext,
  ReactNode,
  useRef,
  RefObject,
  useState,
} from 'react';
import { useMutation } from '@apollo/client';
import { toaster } from '@thinkific/toga-react';

import {
  CreateReplyMutationVariables,
  ReplyEdgeFieldsFragment as CommentType,
} from '__generated__/graphql/legacy/graphql';
import { useCommunityContext } from 'components/CommunityContext';
import { sanitizeAttachments } from 'helpers/postHelpers';
import { RichTextEditorHandle } from 'components/RichTextEditor/RichTextEditor';
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormSetValue,
} from 'react-hook-form';
import { Attachment } from 'schema/Post/types';
import {
  CREATE_REPLY_MUTATION,
  EDIT_REPLY_MUTATION,
} from 'schema/Replies/mutations';
import {
  EVENT_POST_TYPES,
  NUMBER_OF_REACTIONS,
  SUCCESS_TOAST,
} from 'utils/constants';
import { REPLY_FRAGMENT } from 'utils/queries';
import { POST_QUERY } from 'schema/Post/queries';
import { genericToastAlert } from 'utils/toastHelpers';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { rawId } from 'utils/rawId';
import { useTranslation } from 'react-i18next';

interface ReplyEditorContextProviderProps {
  children: ReactNode;
}

export interface FormValues {
  content: string;
  parentPostId?: string;
  parentReplyId?: string;
  attachments: Attachment[];
  mentionedUserIds: string[];
}

interface ReplyEditorContextType {
  control: Control<FormValues>;
  errors: FieldErrors<FormValues>;
  handleSubmit: UseFormHandleSubmit<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  resetForm: () => void;
  onSubmit: (data: FormValues) => void;
  editorRef: RefObject<RichTextEditorHandle>;
  mentionsTextAreaRef: RefObject<HTMLTextAreaElement | HTMLInputElement>;
  isSubmitting: boolean;
  updatePostId: (postId: string) => void;
  updateReplyData: (replyId: string, content: string) => void;
  replyPlaceholder?: string;
  closeReplyOnComment?: () => void;
  isEditing: boolean;
  editingReplyId: string | null;
  startEditing: (comment: CommentType) => void;
  cancelEditing: () => void;
}

const ReplyEditorContext = createContext<ReplyEditorContextType>(
  {} as ReplyEditorContextType
);

const useReplyEditorContext = () => {
  return useContext(ReplyEditorContext);
};

const ReplyEditorProvider = ({ children }: ReplyEditorContextProviderProps) => {
  const { community, trackEvent } = useCommunityContext();
  const { t } = useTranslation();
  const [createReplyMutation, { loading: createReplySubmitting }] = useMutation(
    CREATE_REPLY_MUTATION
  );
  const [editReplyMutation, { loading: editReplySubmitting }] = useMutation(
    EDIT_REPLY_MUTATION
  );
  const editorRef = useRef<RichTextEditorHandle>(null);
  const mentionsTextAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(
    null
  );

  const [replyPlaceholder, setReplyPlaceholder] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      content: '',
      attachments: [],
      mentionedUserIds: [] as string[],
    },
  });

  const startEditing = (comment: CommentType) => {
    const content = comment.content || '';
    setIsEditing(true);
    setEditingReplyId(comment.id);
    setValue('content', content);
    setValue('attachments', (comment.postAttachments as Attachment[]) || []);
    setValue(
      'mentionedUserIds',
      comment.mentionedUsers?.edges?.map((user) => user?.node?.id || '') || []
    );
    setReplyPlaceholder(
      content.length > 60
        ? `${content.slice(0, 60)}...`
        : content || 'Attachments'
    );
    mentionsTextAreaRef.current?.focus();
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditingReplyId(null);
    resetForm();
    mentionsTextAreaRef.current?.focus();
  };

  const resetForm = () => {
    setValue('content', '');
    setValue('attachments', []);
    setValue('mentionedUserIds', []);
    setValue('parentReplyId', '');
    closeReplyOnComment();
  };

  const updatePostId = (postId: string) => {
    setValue('parentPostId', postId);
  };

  const updateReplyData = (replyId: string, content: string) => {
    setValue('parentReplyId', replyId);
    setReplyPlaceholder(
      content.length > 60 ? `${content.slice(0, 60)}...` : content
    );
    mentionsTextAreaRef.current?.focus();
  };

  const closeReplyOnComment = () => {
    setValue('parentReplyId', '');
    setReplyPlaceholder('');
  };

  const onSubmit = async (data) => {
    const {
      content,
      parentReplyId,
      parentPostId,
      attachments,
      mentionedUserIds,
    } = data;

    if (isEditing) {
      const variables = {
        input: {
          id: editingReplyId,
          content,
          attachments: sanitizeAttachments(attachments),
          mentionedUserIds,
        },
        numberOfReactions: NUMBER_OF_REACTIONS,
      };

      try {
        const {
          data: editReplyData,
          errors: editReplyError,
        } = await editReplyMutation({ variables });
        const userErrors = editReplyData?.createPost?.userErrors;
        if (!userErrors?.length && !editReplyError?.length) {
          cancelEditing();
          sendEditTrackingEvent(data);
          resetForm();
          toaster.notice(t(SUCCESS_TOAST.EDIT_REPLY));
        } else {
          genericToastAlert();
        }
      } catch (e) {
        genericToastAlert();
      }
    } else {
      const variables: CreateReplyMutationVariables = {
        discussionableId: community.id,
        content,
        [parentReplyId ? 'parentReplyId' : 'parentPostId']:
          parentReplyId || parentPostId,
        // @ts-ignore
        attachments: sanitizeAttachments(attachments),
        mentionedUserIds,
        numberOfReactions: NUMBER_OF_REACTIONS,
      };
      try {
        const {
          data: createReplyData,
          errors: createReplyError,
        } = await createReplyMutation({
          variables,
          refetchQueries: [
            {
              query: POST_QUERY,
              variables: {
                communityId: community.id,
                postId: parentPostId,
                numberOfReactions: NUMBER_OF_REACTIONS,
              },
            },
          ],
          update(cache, { data: { createReply } }) {
            const { reply } = createReply;
            const parentObject = {
              id: parentReplyId || parentPostId,
              __typename: parentReplyId ? 'Reply' : 'Post',
            };
            cache.modify({
              id: cache.identify(parentObject),
              fields: {
                replies(existingReplies = {}) {
                  const newReplyRef = cache.writeFragment({
                    data: reply,
                    fragment: REPLY_FRAGMENT,
                    fragmentName: 'ReplyFields',
                  });
                  const newEdges = [
                    ...(existingReplies.edges || []),
                    { node: newReplyRef },
                  ];
                  const newTotalCount = existingReplies.totalCount + 1;
                  return {
                    ...existingReplies,
                    totalCount: newTotalCount,
                    edges: newEdges,
                  };
                },
              },
            });
          },
        });

        const userErrors = createReplyData?.createPost?.userErrors;
        if (!userErrors?.length && !createReplyError?.length) {
          toaster.notice(t(SUCCESS_TOAST.CREATE_REPLY));
          sendCreateTrackingEvent(data);

          resetForm();

          editorRef.current?.reset();
        } else {
          genericToastAlert();
        }
      } catch (e) {
        genericToastAlert();
      }
    }
  };

  const sendCreateTrackingEvent = (data) => {
    const { parentPostId, parentReplyId, attachments, mentionedUserIds } = data;
    const {
      ATTACHMENTS,
      POST_TYPE,
      POST_ID,
      POST_ID_ENCODED,
      HAS_MENTION,
    } = TrackingProperty;

    trackEvent(TrackingEvent.REPLY_CREATED, {
      [ATTACHMENTS]: attachments?.map(
        (attachment) => attachment.attachmentType
      ),
      [POST_ID]: parentReplyId ? rawId(parentReplyId) : rawId(parentPostId),
      [POST_ID_ENCODED]: parentReplyId || parentPostId,
      [POST_TYPE]: parentReplyId
        ? EVENT_POST_TYPES.REPLY
        : EVENT_POST_TYPES.COMMENT,
      [HAS_MENTION]: !!mentionedUserIds?.length,
    });
  };

  const sendEditTrackingEvent = (data) => {
    const { parentPostId, parentReplyId, attachments, mentionedUserIds } = data;
    const {
      ATTACHMENTS,
      COMMENT_ID,
      COMMENT_ID_ENCODED,
      POST_TYPE,
      POST_ID,
      POST_ID_ENCODED,
      HAS_MENTION,
    } = TrackingProperty;

    trackEvent(TrackingEvent.EDIT_REPLY, {
      [ATTACHMENTS]: attachments?.map(
        (attachment) => attachment.attachmentType
      ),
      [COMMENT_ID]: rawId(editingReplyId || ''),
      [COMMENT_ID_ENCODED]: editingReplyId || '',
      [POST_ID]: parentReplyId ? rawId(parentReplyId) : rawId(parentPostId),
      [POST_ID_ENCODED]: parentReplyId || parentPostId,
      [POST_TYPE]: parentReplyId
        ? EVENT_POST_TYPES.REPLY
        : EVENT_POST_TYPES.COMMENT,
      [HAS_MENTION]: !!mentionedUserIds?.length,
    });
  };

  return (
    <ReplyEditorContext.Provider
      value={{
        control,
        handleSubmit,
        errors,
        setValue,
        onSubmit,
        resetForm,
        editorRef,
        isSubmitting: createReplySubmitting || editReplySubmitting,
        updatePostId,
        updateReplyData,
        replyPlaceholder,
        closeReplyOnComment,
        isEditing,
        editingReplyId,
        startEditing,
        cancelEditing,
        mentionsTextAreaRef,
      }}
    >
      {children}
    </ReplyEditorContext.Provider>
  );
};

export { ReplyEditorContext, ReplyEditorProvider, useReplyEditorContext };
